import { applyCompanyPlanMessages } from 'features/company-plans/companyPlans.messages';

const baseMessages = {
  en: {
    appName: 'Pacilo Assessment Admin',
    language: { ar: 'العربية', en: 'English' },
    actions: {
      save: 'Save',
      cancel: 'Cancel',
      create: 'Create',
      edit: 'Edit',
      delete: 'Delete',
      archive: 'Archive',
      confirm: 'Confirm',
      retry: 'Retry',
      viewAll: 'View all',
      login: 'Sign in',
      logout: 'Sign out',
      search: 'Search',
      filter: 'Filter',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      publish: 'Publish',
      build: 'Build version',
      preflight: 'Check questionnaire readiness',
      attach: 'Attach',
      detach: 'Detach',
      activate: 'Activate',
      deactivate: 'Deactivate',
      configureWeights: 'Configure weights',
      add: 'Add',
      remove: 'Remove',
      reorder: 'Reorder',
      view: 'View',
      apply: 'Apply',
      assign: 'Assign',
      schedule: 'Schedule',
      complete: 'Complete',
      reject: 'Reject',
      reset: 'Reset filters',
      moveUp: 'Move up',
      moveDown: 'Move down',
      details: 'Details',
      rowActions: 'Row actions',
    },
    layout: {
      skipToContent: 'Skip to main content',
      menu: 'Main menu',
      collapse: 'Collapse navigation',
      expand: 'Expand navigation',
      breadcrumb: 'Breadcrumb',
      language: 'Language',
      profile: 'Administrator profile',
      closeMenu: 'Close navigation',
    },
    nav: {
      overview: 'Overview',
      patterns: 'Professional Patterns',
      builder: 'Sections and Questions',
      weights: 'Pattern Weight Matrix',
      versions: 'Assessment Versions',
      topThreeProfiles: 'Ordered Top-Three Profiles',
      resultContent: 'Result Content',
      attempts: 'User Attempts and Results',
      specialists: 'Career Specialists',
      consultations: 'Career Consultations',
      support: 'Support',
      legalDocuments: 'Legal Documents',
      notifications: 'Notifications',
      menu: 'Main menu',
      breadcrumb: 'Breadcrumb',
      language: 'Language',
      collapse: 'Collapse navigation',
      expand: 'Expand navigation',
      groups: {
        overview: 'Overview',
        assessment: 'Assessment configuration',
        results: 'Results',
        operations: 'Operations',
        governance: 'Governance',
      },
    },
    auth: {
      title: 'Administrator sign in',
      subtitle: 'Sign in with your Laravel administrator account.',
      email: 'Email',
      password: 'Password',
      invalid: 'Invalid credentials.',
      sessionExpired: 'Your session expired. Please sign in again.',
      bootstrapping: 'Restoring administrator session…',
      authorizing: 'Loading administrator permissions…',
      backendUnavailableTitle: 'Backend temporarily unavailable',
      backendUnavailableBody: 'The administrator session is preserved. Retry when the API is reachable again.',
      switchAdmin: 'Sign in as another administrator',
    },
    forbidden: {
      title: 'You do not have permission',
      body: 'This administrator account cannot access that page. Backend authorization remains authoritative.',
      switchAdmin: 'Sign in as another administrator',
    },
    notFound: {
      title: 'Page not found',
      body: 'That URL is not part of the administrator dashboard.',
      home: 'Go to an available page',
    },
    a11y: {
      skipToContent: 'Skip to main content',
      dragStart: 'Picked up {item}',
      dragOver: 'Moved {item} over {over}',
      dragOverNone: '{item} is not over a drop target',
      dragEnd: 'Dropped {item} on {over}',
      dragCancel: 'Cancelled dragging {item}',
    },
    forms: {
      required: 'Required',
      validationSummary: 'Please correct the highlighted fields.',
    },
    empty: {
      generic: 'Nothing to show yet.',
      patterns: 'No professional patterns have been created.',
      sections: 'No assessment sections yet.',
      questions: 'This section has no questions.',
      results: 'No ordered top-three profiles match the current filters.',
      profiles: 'No ordered top-three profiles match the current filters.',
      tags: 'No tags match the current filters.',
      summaries: 'No personal summaries match the current filters.',
      resultItems: 'No result items match the current filters.',
      attempts: 'No user attempts match the current filters.',
      versions: 'No assessment versions have been built.',
      specialists: 'No specialists match the current filters.',
      consultations: 'No consultations match the current filters.',
      supportCategories: 'No support categories have been created.',
      supportTickets: 'No support tickets match the current filters.',
      legalDocuments: 'No legal documents match the current filters.',
    },
    errors: {
      unexpected: 'An unexpected error occurred.',
      network: 'Network failure. Check the API connection and try again.',
      throttled: 'Too many attempts. Please try again later.',
      staleRevision: 'The working configuration has changed. Refresh and review before saving again.',
      validation: 'Validation failed. Review the highlighted fields.',
      forbidden: 'You do not have permission to perform this action.',
      notFound: 'The requested assessment resource was not found.',
      server: 'The server could not complete this request.',
      loadFailed: 'Unable to load this page.',
    },
    status: {
      loading: 'Loading…',
      saving: 'Saving…',
      saved: 'Saved successfully.',
      created: 'Created successfully.',
      updated: 'Updated successfully.',
      deleted: 'Deleted successfully.',
      published: 'Published successfully.',
      generated: 'Profiles created successfully.',
    },
    confirm: {
      title: 'Please confirm',
      delete: 'This cannot be undone from this screen. Continue?',
      leaveUnsaved: 'You have unsaved changes. Leave this page?',
      build: 'Build an immutable version from the current working configuration?',
      publish: 'Publish this version for users? Unpublished working changes will stay unpublished.',
      zeroWeights: 'Every existing answer will contribute 0% for this pattern until you configure weights later.',
      archiveTag: 'This tag will be archived. Existing personal summaries and result items will keep showing it, but it will no longer be selectable for new or updated content.',
      detachSummary: 'Detach this personal summary from the ordered top-three profile?',
      detachItem: 'Detach this item from the ordered top-three profile?',
    },
    overview: {
      title: 'Assessment overview',
      activePatterns: 'Active patterns',
      sections: 'Sections',
      activeQuestions: 'Active questions',
      inactiveQuestions: 'Inactive questions',
      answers: 'Answers',
      pendingWeights: 'Pending / zero weights',
      latestPublished: 'Latest published version',
      workingRevision: 'Working configuration revision',
      topThreeProfiles: 'Ordered top-three profiles',
      generationStatus: 'Generation status',
      completedAttempts: 'Completed attempts',
      recentActivity: 'Completed in the last 7 days',
      nonePublished: 'No assessment version is published.',
      zeroWeights: 'At least one pattern still has zero-initialized weights.',
      staleResults: 'Ordered top-three profile coverage is incomplete, but publishing remains available.',
      workingDiffers: 'The working configuration differs from the latest built version.',
      incompletePublication: 'Publication requirements are incomplete.',
      noRecentActivity: 'The backend did not return a detailed activity feed.',
      quickAccess: 'Quick access',
      activePlans: 'Active company plans',
      activeCompanies: 'Active companies',
      pendingPurchases: 'Pending plan purchases',
      paidRevenue: 'Paid plan revenue',
      availableCredits: 'Available assessment credits',
      activeCodes: 'Active access codes',
      claimedCodes: 'Claimed access codes',
      completedSponsored: 'Completed sponsored assessments',
    },
    patterns: {
      title: 'Professional patterns',
      create: 'New pattern',
      slug: 'Slug',
      nameAr: 'Arabic name',
      nameEn: 'English name',
      descriptionAr: 'Arabic description',
      descriptionEn: 'English description',
      color: 'Color',
      icon: 'Icon',
      iconHint: 'Upload PNG, JPG, SVG, or WebP up to 2 MB.',
      active: 'Active',
      inactive: 'Inactive',
      backfill: 'Weight backfill',
      configureNow: 'Configure weights for all existing answers now',
      zeroNow: 'Initialize all existing answers at 0% and configure later',
      zeroWarning: 'This pattern still has zero-initialized weights.',
      zeroConfirm: 'Every previous answer now contributes 0% for this pattern.',
      progress: 'Weight configuration progress',
      totalAnswers: 'Total answers',
      configured: 'Configured',
      remaining: 'Remaining',
      search: 'Search patterns',
      shortCode: 'Short code',
      shortCodeHint: 'One to four letters. Laravel stores this in uppercase. Identity is not derived from the pattern name.',
      shortCodeImmutable: 'This short code cannot be changed because it is already used in a published version snapshot. Your other edits were kept.',
    },
    sections: {
      edit: 'Edit section',
      titleAr: 'Arabic title',
      titleEn: 'English title',
      descriptionAr: 'Arabic description',
      descriptionEn: 'English description',
    },
    weights: {
      title: 'Pattern weight matrix',
      signedHint: 'Signed percentages from -100.00 to 100.00. Rows do not need to sum to 100%.',
      filterSection: 'Section',
      search: 'Search questions and answers',
      configured: 'Configured',
      unconfigured: 'Unconfigured',
      all: 'All',
      staleConflict: 'A newer working revision exists. Local values were kept. Review and save again.',
      answerEditor: 'All-pattern weights for this answer',
      pending: 'Pending',
      zeroInitialized: 'Zero-initialized',
      missing: 'Unconfigured',
      explicitZero: 'Configured 0',
      configuredNonzero: 'Configured',
      pendingRemaining: 'Some answers remain pending after this save.',
      pageSize: 'Visible rows',
    },
    builder: {
      title: 'Sections and questions',
      newSection: 'New section',
      newQuestion: 'New question',
      questionCount: 'Questions',
      answerCount: 'Answers',
      dragHint: 'Drag to reorder. Moving a question to another section updates both sections in one request.',
      dragHandle: 'Drag to reorder',
      editSection: 'Edit section',
      sectionTitleAr: 'Arabic title',
      sectionTitleEn: 'English title',
      sectionDescriptionAr: 'Arabic description',
      sectionDescriptionEn: 'English description',
    },
    questions: {
      section: 'Section',
      textAr: 'Arabic question',
      textEn: 'English question',
      explanationAr: 'Arabic explanation',
      explanationEn: 'English explanation',
      answers: 'Answers',
      minAnswers: 'At least two answers are required.',
      minActiveAnswers: 'An active question needs at least two active answers.',
      weights: 'Pattern weights',
      answerActive: 'Answer is active',
    },
    answers: {
      detail: 'Answer detail',
      chartSummary: 'Signed radial chart of pattern weights. Positive values extend outward, negative values extend inward, and zero stays on the baseline.',
      lastUpdated: 'Last updated',
    },
    versions: {
      title: 'Assessment versions',
      workingRevision: 'Working revision',
      latestBuilt: 'Latest built version',
      latestPublished: 'Latest published version',
      checksum: 'Configuration checksum',
      canBuild: 'Ready to build',
      cannotBuild: 'Cannot build until blocking warnings are resolved',
      acknowledgeZero: 'Acknowledge zero-initialized pattern weights',
      notPublishable: 'This version cannot be published until version readiness blockers are resolved.',
      preflightStale: 'Working configuration changed. Check questionnaire readiness again before building.',
      label: 'Version label',
      labelPlaceholder: 'Optional label',
      workflow: 'Check questionnaire readiness, build the version so Laravel creates 120 profile shells, then publish. Top-three content connections can be completed independently before or after publication.',
      status: 'Status',
      number: 'Number',
      publishedAt: 'Published at',
      acknowledgeStoredZero: 'Record stored zero-weight acknowledgement',
      storedZeroMissing: 'This built version has no stored zero-weight acknowledgement.',
      storedZeroRecorded: 'Stored zero-weight acknowledgement is recorded on this version.',
      statuses: {
        draft: 'Draft',
        built: 'Built',
        published: 'Published',
        archived: 'Archived',
      },
    },
    results: {
      title: 'Ordered top-three profiles',
      id: 'Profile ID',
      version: 'Version',
      hasSummary: 'Has personal summary',
      noSummary: 'No personal summary',
      missingContent: 'Missing result content',
      snapshotNote: 'Library edits affect future completed attempts. Historical snapshots stay unchanged.',
      tied: 'Tie',
      generationLock: 'A workbook action is already in progress.',
    },
    profiles: {
      description: 'Laravel generates 120 ordered top-three profiles for each assessment version. Administrators configure content connections only.',
      detailTitle: 'Ordered top-three profile {key}',
      identityNote: 'The three-pattern identity is generated by Laravel and cannot be created, deleted, or reordered here.',
      orderedPatterns: 'Ordered top-three patterns',
      firstPattern: 'First pattern',
      secondPattern: 'Second pattern',
      thirdPattern: 'Third pattern',
      canonicalKey: 'Canonical key',
      personalSummary: 'Personal summary',
      strengthCount: 'Strengths',
      growthCount: 'Growth items',
      careerPathCount: 'Career paths',
      skillCount: 'Skills',
      completeness: 'Completeness',
      complete: 'Complete',
      incomplete: 'Incomplete',
      missingContentType: 'Missing content type',
      missingRequired: 'Missing required content',
      coverageTitle: 'Profile coverage',
      profilesCount: 'Profiles',
      profilesFraction: '{found} / {expected}',
      configuredFraction: '{complete} / {expected} configured',
      canPublish: 'Publish allowed',
      coverageComplete: 'Coverage complete',
      coverageIncompleteWarning: 'The ordered top-three profile coverage is incomplete, but it does not prevent publishing the assessment version. The connections can be completed later.',
      publishedReadOnly: 'Archived versions are read-only for profile content.',
      noBuiltVersion: 'There is no built assessment version available for top-three profile configuration.',
      generateOrderings: 'Generate 120 orderings',
      generateBusy: 'Generating ordered top-three profiles…',
      generateSuccess: 'Ordered top-three profiles were generated. Existing identities and relationships were preserved.',
      generateCounts: 'Created {created}, already present {existing}, conflicts {conflicts}, total {total}.',
      generateNeedsSixPatterns: 'Select a version that has exactly six scorable professional patterns.',
      allOrderingsReady: 'All 120 ordered top-three profiles are ready.',
      exportNeedsAllIdentities: 'Export becomes available after all 120 profile identities exist.',
      listLoadFailed: 'The ordered top-three profile list could not be loaded.',
      coverageLoadFailed: 'Profile coverage could not be loaded.',
      contentRevision: 'Profile content revision',
      relationOrderHint: 'Order is stored per relationship. Drag or use move controls. The same item cannot be attached twice in one relation.',
      missing: {
        personalSummary: 'Missing personal summary',
        strength: 'Missing key strength',
        growth: 'Missing growth opportunity',
        careerPath: 'Missing career path',
        skill: 'Missing skill',
      },
    },
    resultContent: {
      title: 'Result Content',
      description: 'Reusable personal summaries, result items, and tags used by ordered top-three profiles.',
      tabs: {
        summaries: 'Personal Summaries',
        keyStrengths: 'Key Strengths',
        growth: 'Challenges and Opportunities for Professional Growth',
        careerPaths: 'Suggested Career Paths',
        skills: 'Skills Worth Developing',
        tags: 'Tags',
      },
      searchTags: 'Search selectable tags',
      filterByTag: 'Filter by tag',
      selectTag: 'Select a tag',
      tagRequired: 'Select at least one active tag.',
      inactiveOrArchivedTag: 'Inactive or archived tags cannot be selected for new content.',
      archivedTag: 'Archived',
      archivedTagReplace: 'The current tag is archived. Choose an active tag before saving changes.',
      skillAdviceRequired: 'Arabic and English skill advice are required for Skills Worth Developing.',
      usageCount: 'Used on {count} ordered top-three profiles',
      dnd: {
        lifted: 'Picked up {title}',
        over: 'Moved {title} over {target}',
        dropped: 'Dropped {title}',
        cancelled: 'Cancelled dragging {title}',
        moved: 'Reordered {title}',
      },
    },
    tags: {
      create: 'Create tag',
      nameAr: 'Arabic name',
      nameEn: 'English name',
      descriptionAr: 'Arabic description',
      descriptionEn: 'English description',
      slug: 'Slug',
      color: 'Color',
      colorHint: 'Optional hex color such as #18A9AA. Color is not the only identifier.',
    },
    summaries: {
      create: 'Create personal summary',
      titleAr: 'Arabic title',
      titleEn: 'English title',
      descriptionAr: 'Arabic description',
      descriptionEn: 'English description',
      tags: 'Tags',
    },
    resultItems: {
      create: 'Create item',
      type: 'Item type',
      titleAr: 'Arabic title',
      titleEn: 'English title',
      descriptionAr: 'Arabic description',
      descriptionEn: 'English description',
      adviceAr: 'Arabic skill advice',
      adviceEn: 'English skill advice',
      skillAdvice: 'Skill advice',
      types: {
        key_strength: 'Key Strength',
        professional_growth_challenge_opportunity: 'Challenges and Opportunities for Professional Growth',
        suggested_career_path: 'Suggested Career Path',
        skill_worth_developing: 'Skill Worth Developing',
      },
    },
    connections: {
      title: 'Result connections',
      oneSummary: 'An ordered top-three profile can have only one personal summary.',
      noSummary: 'No personal summary is attached.',
      noItems: 'No result items are attached.',
      orderedItems: 'Ordered result items',
      globalOrder: 'All attached items share one display order. Dragging changes order only, not item type.',
      searchSummaries: 'Search personal summaries',
      attachSummary: 'Attach personal summary',
      changeSummary: 'Change personal summary',
      createSummaryShortcut: 'Open personal summary library',
      createAndAttachSummary: 'Create and attach personal summary',
      attachItem: 'Attach result items',
      alreadyAttached: 'Already attached',
      alreadyAttachedCount: '{count} matching items are already attached.',
      attachSelected: 'Attach selected items ({count})',
      selectedCount: '{count} selected',
      selectAllVisible: 'Select all visible',
      clearSelection: 'Clear selection',
      removeSelection: 'Remove {title}',
      openPicker: 'Choose result items',
      loadingCatalog: 'Loading result-content catalog…',
      catalogEmpty: 'No reusable result-content items have been created.',
      openResultContent: 'Open Result Content',
      noFilterMatches: 'No items match the selected type, tag, and search.',
      clearFilters: 'Clear filters.',
      allAttached: 'All matching items are already attached to this ordered top-three profile.',
      catalogError: 'The result-content catalog could not be loaded.',
      mixedTypesHint: 'You can select more than one type when the type filter is All.',
      viewRestricted: 'You can view scores for this ordered top-three profile, but result-content connections require additional permission.',
    },
    attempts: {
      title: 'User attempts',
      user: 'User',
      status: 'Status',
      progress: 'Progress',
      started: 'Started',
      completed: 'Completed',
      matchedProfile: 'Resolved top-three profile',
      topPattern: 'Top pattern',
      selectedAnswersUnavailable: 'Selected answers are not included in the administrator attempt contract.',
      historicalContent: 'Historical delivered content',
      snapshotNote: 'This is the content that was delivered to the user. It is a historical snapshot and is not reloaded from the live library.',
      deliveredSummary: 'Delivered personal summary snapshot',
      deliveredItems: 'Delivered result-item snapshots',
      orderedRanking: 'Ordered ranking',
      rank: 'Rank',
      tieBreaking: 'Tie-breaking applied',
      snapshotRevision: 'Content snapshot revision',
    },
    users: {
      deleted: 'Deleted user',
    },
    conflict: {
      title: 'The server rejected this change',
      keepLocal: 'Your unsaved values are still on this page.',
      refresh: 'Refresh server data',
      retry: 'Retry',
    },
    specialists: {
      title: 'Career specialists',
      create: 'New specialist',
      edit: 'Edit specialist',
      detail: 'Specialist',
      nameAr: 'Arabic name',
      nameEn: 'English name',
      titleAr: 'Arabic professional title',
      titleEn: 'English professional title',
      bioAr: 'Arabic biography',
      bioEn: 'English biography',
      uploadImage: 'Upload image',
      replaceImage: 'Replace image',
      removeImage: 'Remove image',
      clearImagePick: 'Clear selected file',
      currentImage: 'Current image',
      avatarUploadHint: 'JPEG, PNG, or WebP up to 2 MB. Laravel stores the uploaded file.',
      avatarInvalidType: 'Choose a JPEG, PNG, or WebP image.',
      avatarTooLarge: 'Image must be 2 MB or smaller.',
      avatarUploadFailed: 'Image upload failed. Review the highlighted field and try again.',
      avatarAlt: 'Portrait of {name}',
      statusUnknown: 'Status unknown',
      statusFilter: 'Filter by status',
      statusActivated: 'Specialist activated.',
      statusDeactivated: 'Specialist deactivated.',
      languages: 'Languages',
      timezone: 'Timezone',
      sortOrder: 'Sort order',
      active: 'Active',
      inactive: 'Inactive',
      search: 'Search specialists',
      cannotDelete: 'This specialist cannot be removed while assigned to an active consultation.',
    },
    consultations: {
      title: 'Career consultations',
      detail: 'Consultation',
      reference: 'Reference',
      user: 'User',
      specialist: 'Specialist',
      status: 'Status',
      goal: 'Goal',
      preferredPeriod: 'Preferred period',
      question: 'User question',
      attempt: 'Linked attempt',
      consent: 'Report-sharing consent',
      consentGranted: 'Granted',
      consentWithdrawn: 'Withdrawn',
      consentDenied: 'Not granted',
      scheduledAt: 'Scheduled at',
      timezone: 'Display timezone',
      meetingUrl: 'Meeting URL',
      communication: 'Communication method',
      history: 'Status history',
      assign: 'Assign specialist',
      schedule: 'Schedule',
      complete: 'Complete',
      reject: 'Reject',
      cancel: 'Cancel',
      reason: 'Reason',
      report: 'Shared assessment report',
      reportHidden: 'The assessment report is hidden because Laravel did not return explicit report-sharing consent.',
      noReschedule: 'Laravel does not expose a reschedule endpoint. Cancel or complete this consultation instead.',
      createdFrom: 'Created from',
      createdTo: 'Created to',
      scheduledFrom: 'Scheduled from',
      scheduledTo: 'Scheduled to',
      statuses: {
        pending_review: 'Pending review',
        matched: 'Matched',
        scheduled: 'Scheduled',
        completed: 'Completed',
        cancelled: 'Cancelled',
        rejected: 'Rejected',
      },
      goals: {
        understand_result: 'Understand result',
        choose_career_or_major: 'Choose career or major',
        change_career_path: 'Change career path',
        develop_current_path: 'Develop current path',
        general_career_consultation: 'General career consultation',
      },
      periods: {
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        flexible: 'Flexible',
      },
      communicationMethods: {
        video: 'Video',
        phone: 'Phone',
        in_app: 'In-app',
      },
    },
    support: {
      title: 'Support',
      categories: 'Categories',
      tickets: 'Tickets',
      category: 'Category',
      createCategory: 'New category',
      editCategory: 'Edit category',
      key: 'Key',
      titleAr: 'Arabic title',
      titleEn: 'English title',
      sortOrder: 'Sort order',
      active: 'Active',
      reference: 'Reference',
      message: 'Message',
      adminResponse: 'Administrative response',
      resolvedAt: 'Resolved at',
      review: 'Move to review',
      resolve: 'Resolve',
      close: 'Close',
      reopen: 'Reopen',
      user: 'User',
      noHistory: 'Laravel does not return ticket status history.',
      statuses: {
        open: 'Open',
        in_review: 'In review',
        resolved: 'Resolved',
        closed: 'Closed',
      },
    },
    legal: {
      title: 'Legal documents',
      create: 'New draft',
      newVersion: 'Create newer draft',
      type: 'Document type',
      version: 'Version',
      titleAr: 'Arabic title',
      titleEn: 'English title',
      contentAr: 'Arabic content',
      contentEn: 'English content',
      effectiveAt: 'Effective date',
      status: 'Status',
      current: 'Current version',
      publishedAt: 'Published at',
      publish: 'Publish',
      publishConfirm: 'Publishing makes this the current version. Laravel then creates one global notification and sends one synchronous FCM topic message. This dashboard does not send push notifications itself.',
      immutable: 'Published documents cannot be edited. Create a newer draft version instead.',
      preview: 'Localized preview',
      noAcceptanceCounts: 'Laravel does not expose acceptance counts on this endpoint.',
      types: {
        terms: 'Terms',
        privacy: 'Privacy',
      },
      statuses: {
        draft: 'Draft',
        published: 'Published',
      },
    },
    notifications: {
      title: 'Notifications',
      body: 'Laravel does not expose an administrator announcement or notification-log API. Publishing a Terms or Privacy document is the supported global announcement path. React never talks to Firebase and never stores FCM credentials.',
      legalLink: 'Open legal documents',
    },
    workbook: {
      questionnaireTitle: 'Questionnaire import and export',
      resultsTitle: 'Top-Three Profile Workbook',
      downloadTemplate: 'Download Question Template',
      exportQuestionnaire: 'Export All Questionnaire Data',
      importQuestionnaire: 'Import Full Questionnaire',
      downloadProfileTemplate: 'Download Excel template',
      exportResults: 'Export current Excel',
      importResults: 'Import Excel',
      existingManagementTitle: 'Existing questionnaire management',
      templateHint: 'The template is for adding questions, answers, weights, and pattern short codes. Laravel generates and parses the XLSX file.',
      fullExportHint: 'The full export can update existing data and add new patterns.',
      fullImportHint: 'Full questionnaire import can synchronize and change existing questionnaire data.',
      previewHint: 'Imports always display a validation preview before database changes.',
      resultsScopeHint: 'The workbook changes Personal Summary and ordered result-content relationships for the 120 ordered top-three profiles. Pattern identities cannot be created, deleted, or reordered.',
      selectedVersion: 'Selected assessment version',
      noVersion: 'No version selected',
      versionRequired: 'Select an assessment version before exporting or importing relationships.',
      versionLocked: 'This version is locked and cannot be imported or exported right now.',
      versionStale: 'This version is stale. Refresh it before using workbook actions.',
      versionIneligible: 'This version is not eligible for workbook import or export.',
      relationshipHelpTitle: 'How Excel represents result relationships',
      selectFile: 'Workbook file',
      dropzone: 'Drop an .xlsx file here or choose one from your computer.',
      fileRestrictions: '.xls, .xlsm, CSV, and password-protected files are unsupported. Only .xlsx workbooks are accepted.',
      removeFile: 'Remove file',
      validatePreview: 'Validate and preview',
      commit: 'Commit import',
      refreshConfiguration: 'Refresh configuration',
      downloadLatestExport: 'Download latest export',
      repreviewRequired: 'The configuration was refreshed. Validate the workbook again before committing.',
      workbookType: 'Workbook type',
      schemaVersion: 'Workbook schema version',
      supportedSchemaVersion: 'Supported schema version',
      baseRevision: 'Export / base revision',
      currentRevision: 'Current server revision',
      canCommit: 'Commit allowed',
      fileHash: 'File hash',
      changeSummary: 'Change summary',
      warnings: 'Warnings',
      warning: 'Warning',
      yes: 'Yes',
      no: 'No',
      newRevision: 'New configuration revision',
      success: 'The workbook was applied successfully.',
      destructiveNote: 'This workbook will delete, archive, or detach records.',
      fullSyncNote: 'This import fully synchronizes all 120 ordered top-three profiles. Existing relationships not present in the workbook will be removed.',
      destructiveConfirm: 'I understand that the listed records and relationships will be changed.',
      filterErrors: 'Filter validation errors',
      relationship: {
        multiRow: 'Many-to-many relationships use one connection row per relationship, not a multi-select cell.',
        dropdowns: 'Each row has a result dropdown, an ordered position, and a content-reference dropdown.',
        position: 'Position controls one global mixed order for Key Strengths, Challenges and Opportunities for Professional Growth, Suggested Career Paths, and Skills Worth Developing.',
        detach: 'Removing a connection row detaches that item from the ordered top-three profile.',
        noMultiSelect: 'Excel does not support multi-select cells for these relationships.',
      },
      sync: {
        immutableKeys: 'Existing records are matched using immutable keys, not titles.',
        newKeys: 'New records use new unique keys.',
        numericZero: 'Numeric 0 is a real zero weight.',
        blankWeight: 'Blank weight cells do not automatically mean zero.',
        newPatternWeights: 'A new Professional Pattern requires weights for all applicable answers.',
        noSilentDelete: 'Missing rows are not silently deleted; deletion or archive must be explicit.',
      },
      results: {
        sync: {
          canonicalKey: 'Each profile is identified by its canonical ordered key, such as R-I-S. R-I-S and I-S-R are different profiles.',
          personalSummaryTitle: 'Select the Personal Summary from the Arabic dropdown. The numeric prefix is the database ID. 0 — بدون ملخص شخصي detaches the summary.',
          relationshipArrays: 'Each relationship column must contain a JSON array of numeric database IDs such as [3,5]. [] clears that category. Blank cells are invalid.',
          catalogIds: 'Find each numeric ID on the matching catalog sheet or in the hidden _Lists sheet. IDs are bound to the exported database and version.',
          blankMeansUnchanged: 'Blank relationship cells are invalid. Every row must contain explicit JSON, including [] when clearing a category.',
          emptyArrayDetaches: '[] clears every relationship of that type. Missing workbook relationships are removed during commit.',
          orderedItemRefs: 'ordered_item_ids is optional. Leave it blank or use [] to let the system derive the order as strengths, growth opportunities, career paths, then skills. A non-empty array defines a custom mixed order.',
          mandatorySync: 'All 120 profile rows always use sync.',
          readOnlyIdentities: 'Triple identities are generated by Laravel when the version is built and cannot be created, deleted, or reordered from Excel.',
          historicalSnapshots: 'Completed user-result snapshots remain historical.',
        },
      },
      importGuidanceTitle: 'Import guidance',
      steps: {
        label: 'Import steps',
        select: 'Select file',
        preview: 'Validate and preview',
        confirm: 'Confirm changes',
        completed: 'Completed',
      },
      status: {
        validating: 'Validating the workbook…',
        committing: 'Applying workbook changes…',
        working: 'Uploading the workbook…',
        uploadProgress: 'Uploading {percent}%',
      },
      summary: {
        patternsCreated: 'Professional Patterns created',
        patternsUpdated: 'Professional Patterns updated',
        patternsArchived: 'Professional Patterns archived',
        patternsDeleted: 'Professional Patterns deleted',
        sectionsCreated: 'Sections created',
        sectionsUpdated: 'Sections updated',
        sectionsMoved: 'Sections moved',
        questionsCreated: 'Questions created',
        questionsUpdated: 'Questions updated',
        questionsMoved: 'Questions moved',
        questionsArchived: 'Questions archived',
        questionsDeleted: 'Questions deleted',
        answersCreated: 'Answers created',
        answersUpdated: 'Answers updated',
        answersArchived: 'Answers archived',
        answersDeleted: 'Answers deleted',
        weightsCreated: 'Weights created',
        weightsUpdated: 'Weights updated',
        unchangedRows: 'Unchanged rows',
        tagsCreated: 'Tags created',
        tagsUpdated: 'Tags updated',
        tagsArchived: 'Tags archived',
        tagsDeleted: 'Tags deleted',
        contentCreated: 'Reusable content created',
        contentUpdated: 'Reusable content updated',
        contentArchived: 'Reusable content archived',
        contentDeleted: 'Reusable content deleted',
        summariesCreated: 'Personal Summaries created',
        summariesUpdated: 'Personal Summaries updated',
        summaryAttached: 'Personal Summary attachments',
        summaryDetached: 'Personal Summary detachments',
        keyStrengthsAttached: 'Key Strength attachments',
        keyStrengthsDetached: 'Key Strength detachments',
        growthAttached: 'Growth Opportunity attachments',
        growthDetached: 'Growth Opportunity detachments',
        careerPathsAttached: 'Career Path attachments',
        careerPathsDetached: 'Career Path detachments',
        skillsAttached: 'Skill attachments',
        skillsDetached: 'Skill detachments',
        connectionsAdded: 'Connections added',
        connectionsRemoved: 'Connections removed',
        connectionsReordered: 'Connections reordered',
        profilesExpected: 'Expected profiles',
        profilesFound: 'Found profiles',
        profilesMatched: 'Profiles matched',
        profilesIgnored: 'Profiles ignored',
        profilesSynchronized: 'Profiles synchronized',
        profilesChanged: 'Profiles whose relationships will change',
        relationshipsRemoved: 'Relationships that will be removed',
        profilesMissing: 'Missing profiles',
        invalidKeys: 'Invalid keys',
        duplicateKeys: 'Duplicate keys',
        unchangedProfiles: 'Unchanged profiles',
      },
      errors: {
        downloadFailed: 'The workbook could not be downloaded. Try again.',
        noExportPermission: 'You do not have permission to export this workbook.',
        noImportPermission: 'You do not have permission to import this workbook.',
        importNotAllowed: 'This version does not allow workbook imports.',
        fileTooLarge: 'The workbook is larger than the server allows.',
        oversized: 'This file is too large to upload from this screen.',
        unsupportedType: 'That workbook type is not supported. Use a standard .xlsx file.',
        unsupportedUploadMediaType: 'The Excel file could not be uploaded because the request format is unsupported. Try again, and contact the administrator if the problem continues.',
        extension: 'Only .xlsx files are accepted. .xls, .xlsm, and CSV files are unsupported.',
        validationTitle: 'The workbook could not be applied. Review the cell-level errors.',
        tableTitle: 'Workbook validation errors',
        sheet: 'Sheet',
        cell: 'Cell',
        message: 'Message',
        value: 'Rejected value',
        staleRevision: 'The database changed after this workbook was exported or previewed.',
        staleInstructions: 'Refresh the configuration, download the latest export if needed, and validate the file again before committing. The selected file is still available.',
        unsupportedSchema: 'This workbook uses schema version {fileVersion}, but this server supports version {supportedVersion}. Download the latest export and validate again. The selected file is still available.',
        networkPreview: 'The preview could not be completed because the connection was interrupted. You can retry validation.',
        commitUnknown: 'The commit result is unknown because the connection was lost. Refresh the configuration and validate again before another commit.',
        malformed: 'The workbook request was malformed. Use a standard .xlsx file generated from the template.',
        wrongWorkbookType: 'This file is not a top-three profile workbook.',
        versionMismatch: 'This workbook belongs to a different assessment version.',
        staleWorkbook: 'This workbook is stale. Download a fresh export and validate again.',
        invalidProfileIdentity: 'A workbook row uses an invalid ordered-profile identity.',
        invalidContentKey: 'A workbook cell uses an invalid result-content key.',
        previewFailed: 'Workbook preview failed. The selected file is still available.',
        previewSuccess: 'Workbook preview completed. No database changes were applied.',
        commitFailed: 'Workbook commit failed. The selected file is still available.',
        commitSuccess: 'Workbook changes were committed successfully.',
      },
      newQuestions: {
        title: 'Add questions using Excel',
        downloadTemplate: 'Download New Questions Template',
        import: 'Import New Questions',
        importTitle: 'Import new questions',
        dialogDescription: 'Preview adds nothing until you confirm. New questions are appended automatically and existing questions stay unchanged.',
        downloadBusy: 'Downloading the new-questions template…',
        downloadSuccess: 'The new-questions template was downloaded.',
        vsFullImport: 'Full import can change existing questionnaire data. New-question import only adds questions.',
        addOnlyHint: 'New-question import never edits or reorders existing questions. Imported questions are appended automatically.',
        existingUnchanged: 'Existing questions are unchanged.',
        appendedAutomatically: 'Questions are appended automatically at the end of the selected sections.',
        explanationSelectSection: 'Select the section from the Arabic dropdown inside Excel.',
        explanationTwoAnswers: 'Add at least two answers for every question.',
        explanationWeights: 'Fill every pattern-weight cell. Blank weights are not treated as zero.',
        explanationAppend: 'Questions are added at the end of each selected section automatically.',
        securityNotice: 'Upload only a standard .xlsx workbook downloaded from this page. Macro-enabled, CSV, .xls, and ZIP files are rejected.',
        selectFile: 'Select workbook',
        replaceFile: 'Replace selected file',
        clearFile: 'Clear selection',
        preview: 'Preview',
        previewing: 'Previewing…',
        previewSummary: 'Preview summary',
        previewState: 'Preview status',
        valid: 'Valid',
        invalid: 'Invalid',
        questionsCount: 'Questions',
        answersCount: 'Answers',
        sectionsCount: 'Affected sections',
        errorsCount: 'Errors',
        warningsCount: 'Warnings',
        questionSummaries: 'Question summaries',
        sectionAr: 'Arabic section',
        answerCount: 'Answer count',
        weightsComplete: 'Pattern weights complete',
        questionRef: 'Question reference',
        excelRow: 'Excel row',
        column: 'Column',
        templateVersion: 'Workbook / template version',
        staleTemplate: 'This template is out of date. Download a fresh template, then preview again. Commit is blocked.',
        patternSetChanged: 'The professional-pattern set changed after this template was generated. Download a fresh template.',
        downloadFresh: 'Download a fresh template',
        commit: 'Commit new questions',
        confirmCopy: 'New questions and answers will be created and appended to the end of the selected sections. Existing questions will not be changed.',
        confirmLabel: 'I understand these questions will be added only; existing questions stay unchanged.',
        success: 'Imported {questions} questions and {answers} answers.',
        failure: 'The new-questions import could not be completed.',
        createdQuestions: 'Questions created',
        createdAnswers: 'Answers created',
        unsupportedFile: 'Unsupported file. Only .xlsx workbooks are accepted.',
        errors: {
          expiredPreview: 'This preview has expired. Upload the workbook and preview it again before committing.',
        },
      },
    },
    pagination: {
      page: 'Page',
      of: 'of',
      perPage: 'Per page',
      previous: 'Previous page',
      next: 'Next page',
    },
    filters: {
      all: 'All',
      active: 'Active',
      inactive: 'Inactive',
    },
  },
  ar: {
    appName: 'لوحة تقييم باكيلو',
    language: { ar: 'العربية', en: 'English' },
    actions: {
      save: 'حفظ',
      cancel: 'إلغاء',
      create: 'إنشاء',
      edit: 'تعديل',
      delete: 'حذف',
      archive: 'أرشفة',
      confirm: 'تأكيد',
      retry: 'إعادة المحاولة',
      viewAll: 'عرض الكل',
      login: 'تسجيل الدخول',
      logout: 'تسجيل الخروج',
      search: 'بحث',
      filter: 'تصفية',
      close: 'إغلاق',
      back: 'رجوع',
      next: 'التالي',
      publish: 'نشر',
      build: 'بناء الإصدار',
      preflight: 'فحص الجاهزية',
      attach: 'إرفاق',
      detach: 'فصل',
      activate: 'تفعيل',
      deactivate: 'إيقاف',
      configureWeights: 'تهيئة الأوزان',
      add: 'إضافة',
      remove: 'إزالة',
      reorder: 'إعادة ترتيب',
      view: 'عرض',
      apply: 'تطبيق',
      assign: 'تعيين',
      schedule: 'جدولة',
      complete: 'إكمال',
      reject: 'رفض',
      reset: 'إعادة ضبط التصفية',
      moveUp: 'تحريك للأعلى',
      moveDown: 'تحريك للأسفل',
      details: 'التفاصيل',
      rowActions: 'إجراءات الصف',
    },
    layout: {
      skipToContent: 'تخطَّ إلى المحتوى الرئيسي',
      menu: 'القائمة الرئيسية',
      collapse: 'طي التنقل',
      expand: 'توسيع التنقل',
      breadcrumb: 'مسار التنقل',
      language: 'اللغة',
      profile: 'ملف المسؤول',
      closeMenu: 'إغلاق التنقل',
    },
    nav: {
      overview: 'نظرة عامة',
      patterns: 'الأنماط المهنية',
      builder: 'الأقسام والأسئلة',
      weights: 'مصفوفة أوزان الأنماط',
      versions: 'إصدارات التقييم',
      topThreeProfiles: 'روابط أعلى ثلاثة أنماط',
      resultContent: 'محتوى النتائج',
      attempts: 'محاولات المستخدمين والنتائج',
      specialists: 'الأخصائيون المهنيون',
      consultations: 'الاستشارات المهنية',
      support: 'الدعم',
      legalDocuments: 'المستندات القانونية',
      notifications: 'الإشعارات',
      menu: 'القائمة الرئيسية',
      breadcrumb: 'مسار التنقل',
      language: 'اللغة',
      collapse: 'طي التنقل',
      expand: 'توسيع التنقل',
      groups: {
        overview: 'نظرة عامة',
        assessment: 'تهيئة التقييم',
        results: 'النتائج',
        operations: 'العمليات',
        governance: 'الحوكمة',
      },
    },
    auth: {
      title: 'دخول المسؤول',
      subtitle: 'سجّل الدخول بحساب مسؤول Laravel.',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      invalid: 'بيانات الاعتماد غير صحيحة.',
      sessionExpired: 'انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.',
      bootstrapping: 'جارٍ استعادة جلسة المسؤول…',
      authorizing: 'جارٍ تحميل صلاحيات المسؤول…',
      backendUnavailableTitle: 'الخادم غير متاح مؤقتًا',
      backendUnavailableBody: 'تم الاحتفاظ بجلسة المسؤول. أعد المحاولة عندما يصبح واجهة برمجة التطبيقات متاحة.',
      switchAdmin: 'تسجيل الدخول بمسؤول آخر',
    },
    forbidden: {
      title: 'ليست لديك صلاحية',
      body: 'لا يمكن لهذا الحساب الوصول إلى هذه الصفحة. صلاحيات الخادم تبقى المرجع النهائي.',
      switchAdmin: 'تسجيل الدخول بمسؤول آخر',
    },
    notFound: {
      title: 'الصفحة غير موجودة',
      body: 'هذا المسار لا يطابق صفحة مسؤول.',
      home: 'الانتقال إلى صفحة متاحة',
    },
    a11y: {
      skipToContent: 'تخطَّ إلى المحتوى الرئيسي',
      dragStart: 'تم التقاط {item}',
      dragOver: 'نُقل {item} فوق {over}',
      dragOverNone: '{item} ليس فوق هدف إسقاط',
      dragEnd: 'أُسقط {item} على {over}',
      dragCancel: 'أُلغي سحب {item}',
    },
    forms: {
      required: 'مطلوب',
      validationSummary: 'يرجى تصحيح الحقول المظللة.',
    },
    empty: {
      generic: 'لا توجد بيانات حتى الآن.',
      patterns: 'لم يتم إنشاء أنماط مهنية بعد.',
      sections: 'لا توجد أقسام بعد.',
      questions: 'لا توجد أسئلة في هذا القسم.',
      results: 'لا توجد روابط أعلى ثلاثة أنماط مطابقة للتصفية الحالية.',
      profiles: 'لا توجد روابط أعلى ثلاثة أنماط مطابقة للتصفية الحالية.',
      tags: 'لا توجد وسوم مطابقة للتصفية الحالية.',
      summaries: 'لا توجد ملخصات شخصية مطابقة للتصفية الحالية.',
      resultItems: 'لا توجد عناصر نتائج مطابقة للتصفية الحالية.',
      attempts: 'لا توجد محاولات مطابقة للتصفية الحالية.',
      versions: 'لم يتم بناء أي إصدار بعد.',
      specialists: 'لا يوجد أخصائيون مطابقون للتصفية الحالية.',
      consultations: 'لا توجد استشارات مطابقة للتصفية الحالية.',
      supportCategories: 'لم يتم إنشاء فئات دعم بعد.',
      supportTickets: 'لا توجد تذاكر مطابقة للتصفية الحالية.',
      legalDocuments: 'لا توجد مستندات قانونية مطابقة للتصفية الحالية.',
    },
    errors: {
      unexpected: 'حدث خطأ غير متوقع.',
      network: 'فشل الاتصال بالشبكة. تحقق من واجهة البرمجة ثم أعد المحاولة.',
      throttled: 'محاولات كثيرة. حاول لاحقاً.',
      staleRevision: 'تغيرت تهيئة العمل. حدّث البيانات وراجعها قبل الحفظ.',
      validation: 'فشل التحقق. راجع الحقول المحددة.',
      forbidden: 'ليست لديك صلاحية لتنفيذ هذا الإجراء.',
      notFound: 'المورد المطلوب غير موجود.',
      server: 'تعذر على الخادم إكمال الطلب.',
      loadFailed: 'تعذر تحميل هذه الصفحة.',
    },
    status: {
      loading: 'جارٍ التحميل…',
      saving: 'جارٍ الحفظ…',
      saved: 'تم الحفظ بنجاح.',
      created: 'تم الإنشاء بنجاح.',
      updated: 'تم التحديث بنجاح.',
      deleted: 'تم الحذف بنجاح.',
      published: 'تم النشر بنجاح.',
      generated: 'تم إنشاء الملفات بنجاح.',
    },
    confirm: {
      title: 'يرجى التأكيد',
      delete: 'لا يمكن التراجع عن هذا الإجراء من هذه الشاشة. هل تريد المتابعة؟',
      leaveUnsaved: 'لديك تغييرات غير محفوظة. هل تريد مغادرة الصفحة؟',
      build: 'بناء إصدار ثابت من تهيئة العمل الحالية؟',
      publish: 'نشر هذا الإصدار للمستخدمين؟ تبقى تغييرات العمل غير المنشورة غير منشورة.',
      zeroWeights: 'ستساهم كل الإجابات الحالية بنسبة 0% لهذا النمط إلى أن تتم تهيئة الأوزان لاحقاً.',
      archiveTag: 'سيتم أرشفة هذا الوسم. ستستمر الملخصات الشخصية وعناصر النتائج الحالية في عرضه، لكنه لن يظهر كخيار قابل للاختيار للمحتوى الجديد أو المحدّث.',
      detachSummary: 'فصل هذا الملخص الشخصي عن رابط أعلى ثلاثة أنماط؟',
      detachItem: 'فصل هذا العنصر عن رابط أعلى ثلاثة أنماط؟',
    },
    overview: {
      title: 'نظرة عامة على التقييم',
      activePatterns: 'الأنماط النشطة',
      sections: 'الأقسام',
      activeQuestions: 'الأسئلة النشطة',
      inactiveQuestions: 'الأسئلة غير النشطة',
      answers: 'الإجابات',
      pendingWeights: 'أوزان معلقة / صفرية',
      latestPublished: 'آخر إصدار منشور',
      workingRevision: 'مراجعة تهيئة العمل',
      topThreeProfiles: 'روابط أعلى ثلاثة أنماط',
      generationStatus: 'حالة التوليد',
      completedAttempts: 'المحاولات المكتملة',
      recentActivity: 'مكتمل خلال 7 أيام',
      nonePublished: 'لا يوجد إصدار منشور.',
      zeroWeights: 'لا يزال هناك نمط بأوزان صفرية غير مهيأة.',
      staleResults: 'تغطية روابط أعلى ثلاثة أنماط غير مكتملة، لكن النشر يبقى متاحاً.',
      workingDiffers: 'تهيئة العمل تختلف عن آخر إصدار مبني.',
      incompletePublication: 'متطلبات النشر غير مكتملة.',
      noRecentActivity: 'لم يُرجع الخادم موجزاً تفصيلياً للنشاط.',
      quickAccess: 'وصول سريع',
      activePlans: 'خطط الشركات النشطة',
      activeCompanies: 'الشركات النشطة',
      pendingPurchases: 'مشتريات الخطط المعلقة',
      paidRevenue: 'إيراد الخطط المدفوعة',
      availableCredits: 'رصيد التقييمات المتاح',
      activeCodes: 'رموز الوصول النشطة',
      claimedCodes: 'رموز الوصول المستخدمة',
      completedSponsored: 'التقييمات الممولة المكتملة',
    },
    patterns: {
      title: 'الأنماط المهنية',
      create: 'نمط جديد',
      slug: 'المعرّف النصي',
      nameAr: 'الاسم بالعربية',
      nameEn: 'الاسم بالإنجليزية',
      descriptionAr: 'الوصف بالعربية',
      descriptionEn: 'الوصف بالإنجليزية',
      color: 'اللون',
      icon: 'الأيقونة',
      iconHint: 'ارفع PNG أو JPG أو SVG أو WebP حتى 2 ميجابايت.',
      active: 'نشط',
      inactive: 'غير نشط',
      backfill: 'تهيئة الأوزان',
      configureNow: 'تهيئة أوزان كل الإجابات الحالية الآن',
      zeroNow: 'تعيين كل الإجابات الحالية إلى 0% والتهيئة لاحقاً',
      zeroWarning: 'لا يزال هذا النمط يحتوي أوزاناً صفرية مهيأة مؤقتاً.',
      zeroConfirm: 'كل الإجابات السابقة أصبحت تساهم بنسبة 0% لهذا النمط.',
      progress: 'تقدم تهيئة الأوزان',
      totalAnswers: 'إجمالي الإجابات',
      configured: 'مهيأ',
      remaining: 'متبقي',
      search: 'بحث في الأنماط',
      shortCode: 'الرمز المختصر',
      shortCodeHint: 'من حرف إلى أربعة أحرف. يخزّنه Laravel بأحرف كبيرة. لا تُشتق الهوية من اسم النمط.',
      shortCodeImmutable: 'لا يمكن تغيير هذا الرمز لأنه مستخدم في لقطة إصدار منشور. بقيت تعديلاتك الأخرى في الصفحة.',
    },
    sections: {
      edit: 'تعديل القسم',
      titleAr: 'العنوان بالعربية',
      titleEn: 'العنوان بالإنجليزية',
      descriptionAr: 'الوصف بالعربية',
      descriptionEn: 'الوصف بالإنجليزية',
    },
    weights: {
      title: 'مصفوفة أوزان النمط',
      signedHint: 'نسب موقعة من -100.00 إلى 100.00. لا يُشترط أن يساوي مجموع الصف 100%.',
      filterSection: 'القسم',
      search: 'بحث في الأسئلة والإجابات',
      configured: 'مهيأ',
      unconfigured: 'غير مهيأ',
      all: 'الكل',
      staleConflict: 'توجد مراجعة أحدث. تم الاحتفاظ بالقيم المحلية. راجع ثم احفظ مرة أخرى.',
      answerEditor: 'أوزان كل الأنماط لهذه الإجابة',
      pending: 'معلق',
      zeroInitialized: 'صفري مهيأ',
      missing: 'غير مهيأ',
      explicitZero: 'مهيأ بقيمة 0',
      configuredNonzero: 'مهيأ',
      pendingRemaining: 'لا تزال بعض الإجابات معلّقة بعد هذا الحفظ.',
      pageSize: 'الصفوف الظاهرة',
    },
    builder: {
      title: 'الأقسام والأسئلة',
      newSection: 'قسم جديد',
      newQuestion: 'سؤال جديد',
      questionCount: 'الأسئلة',
      answerCount: 'الإجابات',
      dragHint: 'اسحب لإعادة الترتيب. نقل سؤال إلى قسم آخر يحدّث القسمين في طلب واحد.',
      dragHandle: 'اسحب لإعادة الترتيب',
      editSection: 'تعديل القسم',
    },
    questions: {
      section: 'القسم',
      textAr: 'نص السؤال بالعربية',
      textEn: 'نص السؤال بالإنجليزية',
      explanationAr: 'الشرح بالعربية',
      explanationEn: 'الشرح بالإنجليزية',
      answers: 'الإجابات',
      minAnswers: 'يلزم وجود إجابتين على الأقل.',
      minActiveAnswers: 'السؤال النشط يحتاج إجابتين نشطتين على الأقل.',
      weights: 'أوزان الأنماط',
      answerActive: 'الإجابة نشطة',
    },
    answers: {
      detail: 'تفاصيل الإجابة',
      chartSummary: 'رسم شعاعي موقّع لأوزان الأنماط. القيم الموجبة تمتد للخارج والسالبة للداخل والصفر يبقى على الخط الأساس.',
      lastUpdated: 'آخر تحديث',
    },
    versions: {
      title: 'إصدارات التقييم',
      workingRevision: 'مراجعة العمل',
      latestBuilt: 'آخر إصدار مبني',
      latestPublished: 'آخر إصدار منشور',
      checksum: 'بصمة التهيئة',
      canBuild: 'جاهز للبناء',
      cannotBuild: 'لا يمكن البناء حتى تُحل التحذيرات المانعة',
      acknowledgeZero: 'الإقرار بالأوزان الصفرية المهيأة',
      notPublishable: 'لا يمكن نشر هذا الإصدار حتى تُحل موانع جاهزية الإصدار.',
      preflightStale: 'تغيرت تهيئة العمل. أعد فحص جاهزية الاستبيان قبل البناء.',
      label: 'تسمية الإصدار',
      labelPlaceholder: 'تسمية اختيارية',
      workflow: 'افحص جاهزية الاستبيان، ثم ابنِ الإصدار لينشئ Laravel 120 غلاف ملف، ثم انشر. يمكن استكمال روابط أعلى ثلاثة أنماط بشكل مستقل قبل النشر أو بعده.',
      status: 'الحالة',
      number: 'الرقم',
      publishedAt: 'تاريخ النشر',
      acknowledgeStoredZero: 'تسجيل إقرار الأوزان الصفرية المخزّن',
      storedZeroMissing: 'هذا الإصدار المبني لا يحتوي على إقرار مخزّن بالأوزان الصفرية.',
      storedZeroRecorded: 'تم تسجيل إقرار الأوزان الصفرية على هذا الإصدار.',
      statuses: {
        draft: 'مسودة',
        built: 'مبني',
        published: 'منشور',
        archived: 'مؤرشف',
      },
    },
    results: {
      title: 'روابط أعلى ثلاثة أنماط',
      id: 'معرّف الملف',
      version: 'الإصدار',
      hasSummary: 'يوجد ملخص شخصي',
      noSummary: 'لا يوجد ملخص شخصي',
      missingContent: 'محتوى النتائج ناقص',
      snapshotNote: 'تعديل المكتبة يؤثر على المحاولات المكتملة لاحقاً. اللقطات التاريخية تبقى كما هي.',
      tied: 'تعادل',
      generationLock: 'يوجد إجراء مصنف قيد التنفيذ حالياً.',
    },
    profiles: {
      description: 'يولّد Laravel 120 رابطاً مرتباً لأعلى ثلاثة أنماط لكل إصدار تقييم. يضبط المسؤول اتصالات المحتوى فقط.',
      detailTitle: 'رابط أعلى ثلاثة أنماط {key}',
      identityNote: 'هوية الأنماط الثلاثة يولّدها Laravel ولا يمكن إنشاؤها أو حذفها أو إعادة ترتيبها هنا.',
      orderedPatterns: 'الأنماط الثلاثة المرتبة',
      firstPattern: 'النمط الأول',
      secondPattern: 'النمط الثاني',
      thirdPattern: 'النمط الثالث',
      canonicalKey: 'المفتاح المعياري',
      personalSummary: 'الملخص الشخصي',
      strengthCount: 'نقاط القوة',
      growthCount: 'عناصر النمو',
      careerPathCount: 'المسارات المهنية',
      skillCount: 'المهارات',
      completeness: 'الاكتمال',
      complete: 'مكتمل',
      incomplete: 'غير مكتمل',
      missingContentType: 'نوع المحتوى الناقص',
      missingRequired: 'محتوى مطلوب ناقص',
      coverageTitle: 'تغطية الملفات',
      profilesCount: 'الملفات',
      profilesFraction: '{found} / {expected}',
      configuredFraction: '{complete} / {expected} مهيأ',
      canPublish: 'يُسمح بالنشر',
      coverageComplete: 'اكتملت التغطية',
      coverageIncompleteWarning: 'تغطية روابط أعلى ثلاثة أنماط غير مكتملة، لكنها لا تمنع نشر إصدار التقييم. يمكن استكمال الروابط لاحقاً.',
      publishedReadOnly: 'الإصدارات المؤرشفة للقراءة فقط بالنسبة لمحتوى الملفات.',
      noBuiltVersion: 'لا يوجد إصدار مبني لعرض روابط الأنماط الثلاثة.',
      generateOrderings: 'توليد 120 ترتيباً',
      generateBusy: 'جارٍ توليد روابط أعلى ثلاثة أنماط…',
      generateSuccess: 'تم توليد روابط أعلى ثلاثة أنماط. بقيت الهويات والعلاقات الحالية محفوظة.',
      generateCounts: 'أُنشئ {created}، موجود مسبقاً {existing}، تعارضات {conflicts}، الإجمالي {total}.',
      generateNeedsSixPatterns: 'اختر إصداراً يحتوي على ستة أنماط مهنية قابلة للتسجيل بالضبط.',
      allOrderingsReady: 'أصبحت الترتيبات الـ 120 كلها جاهزة.',
      exportNeedsAllIdentities: 'يتاح التصدير بعد وجود الهويات الـ 120 كلها.',
      listLoadFailed: 'تعذر تحميل قائمة روابط أعلى ثلاثة أنماط.',
      coverageLoadFailed: 'تعذر تحميل تغطية الملفات.',
      contentRevision: 'مراجعة محتوى النمط',
      relationOrderHint: 'يُحفظ الترتيب لكل علاقة. اسحب أو استخدم أزرار النقل. لا يمكن إرفاق العنصر نفسه مرتين في العلاقة الواحدة.',
      missing: {
        personalSummary: 'الملخص الشخصي ناقص',
        strength: 'نقطة القوة ناقصة',
        growth: 'فرصة النمو ناقصة',
        careerPath: 'المسار المهني ناقص',
        skill: 'المهارة ناقصة',
      },
    },
    resultContent: {
      title: 'محتوى النتائج',
      description: 'ملخصات شخصية وعناصر نتائج ووسوم قابلة لإعادة الاستخدام لروابط أعلى ثلاثة أنماط.',
      tabs: {
        summaries: 'الملخصات الشخصية',
        keyStrengths: 'نقاط القوة الرئيسية',
        growth: 'التحديات وفرص النمو المهني',
        careerPaths: 'المسارات المهنية المقترحة',
        skills: 'مهارات تستحق التطوير',
        tags: 'الوسوم',
      },
      searchTags: 'بحث في الوسوم القابلة للاختيار',
      filterByTag: 'تصفية حسب الوسم',
      selectTag: 'اختر وسماً',
      tagRequired: 'يلزم اختيار وسم نشط واحد على الأقل.',
      inactiveOrArchivedTag: 'لا يمكن اختيار الوسوم غير النشطة أو المؤرشفة للمحتوى الجديد.',
      archivedTag: 'مؤرشف',
      archivedTagReplace: 'الوسم الحالي مؤرشف. اختر وسماً نشطاً قبل حفظ التغييرات.',
      skillAdviceRequired: 'نصيحة المهارة بالعربية والإنجليزية مطلوبة لمهارات تستحق التطوير.',
      usageCount: 'مستخدم في {count} روابط أعلى ثلاثة أنماط',
      dnd: {
        lifted: 'تم التقاط {title}',
        over: 'تم نقل {title} فوق {target}',
        dropped: 'تم إفلات {title}',
        cancelled: 'تم إلغاء سحب {title}',
        moved: 'تم إعادة ترتيب {title}',
      },
    },
    tags: {
      create: 'إنشاء وسم',
      nameAr: 'الاسم بالعربية',
      nameEn: 'الاسم بالإنجليزية',
      descriptionAr: 'الوصف بالعربية',
      descriptionEn: 'الوصف بالإنجليزية',
      slug: 'المعرّف النصي',
      color: 'اللون',
      colorHint: 'لون اختياري بصيغة hex مثل #18A9AA. اللون ليس المعرّف الوحيد.',
    },
    summaries: {
      create: 'إنشاء ملخص شخصي',
      titleAr: 'العنوان بالعربية',
      titleEn: 'العنوان بالإنجليزية',
      descriptionAr: 'الوصف بالعربية',
      descriptionEn: 'الوصف بالإنجليزية',
      tags: 'الوسوم',
    },
    resultItems: {
      create: 'إنشاء عنصر',
      type: 'نوع العنصر',
      titleAr: 'العنوان بالعربية',
      titleEn: 'العنوان بالإنجليزية',
      descriptionAr: 'الوصف بالعربية',
      descriptionEn: 'الوصف بالإنجليزية',
      adviceAr: 'نصيحة المهارة بالعربية',
      adviceEn: 'نصيحة المهارة بالإنجليزية',
      skillAdvice: 'نصيحة المهارة',
      types: {
        key_strength: 'نقطة قوة رئيسية',
        professional_growth_challenge_opportunity: 'التحديات وفرص النمو المهني',
        suggested_career_path: 'مسار مهني مقترح',
        skill_worth_developing: 'مهارة تستحق التطوير',
      },
    },
    connections: {
      title: 'ارتباطات النتيجة',
      oneSummary: 'يمكن لرابط أعلى ثلاثة أنماط أن يحتوي ملخصاً شخصياً واحداً فقط.',
      noSummary: 'لا يوجد ملخص شخصي مرفق.',
      noItems: 'لا توجد عناصر نتائج مرفقة.',
      orderedItems: 'عناصر النتائج المرتبة',
      globalOrder: 'كل العناصر المرفقة تشترك في ترتيب عرض واحد. السحب يغيّر الترتيب فقط وليس نوع العنصر.',
      searchSummaries: 'بحث في الملخصات الشخصية',
      attachSummary: 'إرفاق ملخص شخصي',
      changeSummary: 'تغيير الملخص الشخصي',
      createSummaryShortcut: 'فتح مكتبة الملخصات الشخصية',
      createAndAttachSummary: 'إنشاء ملخص شخصي وإرفاقه',
      attachItem: 'إرفاق عناصر النتائج',
      alreadyAttached: 'مرفق مسبقاً',
      alreadyAttachedCount: '{count} عناصر مطابقة مرفقة مسبقاً.',
      attachSelected: 'إرفاق العناصر المحددة ({count})',
      selectedCount: '{count} محددة',
      selectAllVisible: 'تحديد كل الظاهر',
      clearSelection: 'مسح التحديد',
      removeSelection: 'إزالة {title}',
      openPicker: 'اختيار عناصر النتائج',
      loadingCatalog: 'جارٍ تحميل عناصر النتائج…',
      catalogEmpty: 'لم تتم إضافة عناصر نتائج قابلة لإعادة الاستخدام.',
      openResultContent: 'فتح محتوى النتائج',
      noFilterMatches: 'لا توجد عناصر تطابق النوع والوسم والبحث المحدد.',
      clearFilters: 'مسح عوامل التصفية.',
      allAttached: 'جميع العناصر المطابقة مرتبطة برابط أعلى ثلاثة أنماط هذا بالفعل.',
      catalogError: 'تعذر تحميل عناصر النتائج.',
      mixedTypesHint: 'يمكن اختيار أكثر من نوع عندما يكون عامل النوع هو الكل.',
      viewRestricted: 'يمكنك عرض الدرجات لرابط أعلى ثلاثة أنماط هذا، لكن ارتباطات محتوى النتائج تتطلب صلاحية إضافية.',
    },
    attempts: {
      title: 'محاولات المستخدمين',
      user: 'المستخدم',
      status: 'الحالة',
      progress: 'التقدم',
      started: 'البداية',
      completed: 'الاكتمال',
      matchedProfile: 'رابط أعلى ثلاثة أنماط المحلول',
      topPattern: 'النمط الأعلى',
      selectedAnswersUnavailable: 'الإجابات المختارة غير مضمّنة في عقد محاولة المسؤول.',
      historicalContent: 'المحتوى التاريخي المسلّم',
      snapshotNote: 'هذا هو المحتوى الذي سُلّم للمستخدم. إنها لقطة تاريخية ولا تُعاد من المكتبة الحيّة.',
      deliveredSummary: 'لقطة الملخص الشخصي المسلّم',
      deliveredItems: 'لقطات عناصر النتائج المسلّمة',
      orderedRanking: 'الترتيب المرتب',
      rank: 'الرتبة',
      tieBreaking: 'تم تطبيق كسر التعادل',
      snapshotRevision: 'مراجعة لقطة المحتوى',
    },
    users: {
      deleted: 'مستخدم محذوف',
    },
    conflict: {
      title: 'رفض الخادم هذا التغيير',
      keepLocal: 'ما زالت القيم غير المحفوظة ظاهرة في هذه الصفحة.',
      refresh: 'تحديث بيانات الخادم',
      retry: 'إعادة المحاولة',
    },
    specialists: {
      title: 'الأخصائيون المهنيون',
      create: 'أخصائي جديد',
      edit: 'تعديل الأخصائي',
      detail: 'الأخصائي',
      nameAr: 'الاسم بالعربية',
      nameEn: 'الاسم بالإنجليزية',
      titleAr: 'المسمى المهني بالعربية',
      titleEn: 'المسمى المهني بالإنجليزية',
      bioAr: 'السيرة بالعربية',
      bioEn: 'السيرة بالإنجليزية',
      uploadImage: 'رفع صورة',
      replaceImage: 'استبدال الصورة',
      removeImage: 'إزالة الصورة',
      clearImagePick: 'مسح الملف المحدد',
      currentImage: 'الصورة الحالية',
      avatarUploadHint: 'JPEG أو PNG أو WebP حتى 2 ميجابايت. Laravel يخزّن الملف المرفوع.',
      avatarInvalidType: 'اختر صورة JPEG أو PNG أو WebP.',
      avatarTooLarge: 'يجب ألا يتجاوز حجم الصورة 2 ميجابايت.',
      avatarUploadFailed: 'فشل رفع الصورة. راجع الحقل المظلل ثم حاول مرة أخرى.',
      avatarAlt: 'صورة {name}',
      statusUnknown: 'حالة غير معروفة',
      statusFilter: 'تصفية حسب الحالة',
      statusActivated: 'تم تفعيل الأخصائي.',
      statusDeactivated: 'تم إلغاء تفعيل الأخصائي.',
      languages: 'اللغات',
      timezone: 'المنطقة الزمنية',
      sortOrder: 'ترتيب العرض',
      active: 'نشط',
      inactive: 'غير نشط',
      search: 'بحث في الأخصائيين',
      cannotDelete: 'لا يمكن حذف هذا الأخصائي بينما هو معيّن على استشارة نشطة.',
    },
    consultations: {
      title: 'الاستشارات المهنية',
      detail: 'الاستشارة',
      reference: 'المرجع',
      user: 'المستخدم',
      specialist: 'الأخصائي',
      status: 'الحالة',
      goal: 'الهدف',
      preferredPeriod: 'الفترة المفضلة',
      question: 'سؤال المستخدم',
      attempt: 'المحاولة المرتبطة',
      consent: 'موافقة مشاركة التقرير',
      consentGranted: 'ممنوحة',
      consentWithdrawn: 'مسحوبة',
      consentDenied: 'غير ممنوحة',
      scheduledAt: 'موعد الجدولة',
      timezone: 'المنطقة الزمنية للعرض',
      meetingUrl: 'رابط الاجتماع',
      communication: 'طريقة التواصل',
      history: 'سجل الحالات',
      assign: 'تعيين أخصائي',
      schedule: 'جدولة',
      complete: 'إكمال',
      reject: 'رفض',
      cancel: 'إلغاء',
      reason: 'السبب',
      report: 'تقرير التقييم المشترك',
      reportHidden: 'التقرير مخفي لأن Laravel لم يُرجع موافقة صريحة على مشاركة التقرير.',
      noReschedule: 'Laravel لا يوفّر نقطة إعادة جدولة. يمكن الإلغاء أو الإكمال بدلاً من ذلك.',
      createdFrom: 'أُنشئ من',
      createdTo: 'أُنشئ إلى',
      scheduledFrom: 'مجدول من',
      scheduledTo: 'مجدول إلى',
      statuses: {
        pending_review: 'قيد المراجعة',
        matched: 'مطابقة',
        scheduled: 'مجدولة',
        completed: 'مكتملة',
        cancelled: 'ملغاة',
        rejected: 'مرفوضة',
      },
      goals: {
        understand_result: 'فهم النتيجة',
        choose_career_or_major: 'اختيار مسار أو تخصص',
        change_career_path: 'تغيير المسار المهني',
        develop_current_path: 'تطوير المسار الحالي',
        general_career_consultation: 'استشارة مهنية عامة',
      },
      periods: {
        morning: 'صباحاً',
        afternoon: 'ظهراً',
        evening: 'مساءً',
        flexible: 'مرن',
      },
      communicationMethods: {
        video: 'فيديو',
        phone: 'هاتف',
        in_app: 'داخل التطبيق',
      },
    },
    support: {
      title: 'الدعم',
      categories: 'الفئات',
      tickets: 'التذاكر',
      category: 'الفئة',
      createCategory: 'فئة جديدة',
      editCategory: 'تعديل الفئة',
      key: 'المفتاح',
      titleAr: 'العنوان بالعربية',
      titleEn: 'العنوان بالإنجليزية',
      sortOrder: 'ترتيب العرض',
      active: 'نشطة',
      reference: 'المرجع',
      message: 'الرسالة',
      adminResponse: 'رد الإدارة',
      resolvedAt: 'تاريخ الحل',
      review: 'نقل للمراجعة',
      resolve: 'حل',
      close: 'إغلاق',
      reopen: 'إعادة فتح',
      user: 'المستخدم',
      noHistory: 'Laravel لا يُرجع سجل حالات التذكرة.',
      statuses: {
        open: 'مفتوحة',
        in_review: 'قيد المراجعة',
        resolved: 'محلولة',
        closed: 'مغلقة',
      },
    },
    legal: {
      title: 'المستندات القانونية',
      create: 'مسودة جديدة',
      newVersion: 'إنشاء مسودة أحدث',
      type: 'نوع المستند',
      version: 'الإصدار',
      titleAr: 'العنوان بالعربية',
      titleEn: 'العنوان بالإنجليزية',
      contentAr: 'المحتوى بالعربية',
      contentEn: 'المحتوى بالإنجليزية',
      effectiveAt: 'تاريخ السريان',
      status: 'الحالة',
      current: 'الإصدار الحالي',
      publishedAt: 'تاريخ النشر',
      publish: 'نشر',
      publishConfirm: 'النشر يجعل هذا الإصدار الحالي. ثم ينشئ Laravel إشعاراً عاماً واحداً ويرسل رسالة FCM موضوعية متزامنة واحدة. لوحة التحكم لا ترسل الإشعارات بنفسها.',
      immutable: 'لا يمكن تعديل المستندات المنشورة. أنشئ مسودة أحدث بدلاً من ذلك.',
      preview: 'معاينة موطّنة',
      noAcceptanceCounts: 'Laravel لا يعرض أعداد القبول على هذه النقطة.',
      types: {
        terms: 'الشروط',
        privacy: 'الخصوصية',
      },
      statuses: {
        draft: 'مسودة',
        published: 'منشور',
      },
    },
    notifications: {
      title: 'الإشعارات',
      body: 'Laravel لا يوفّر واجهة إدارة للإعلانات أو سجل الإشعارات. نشر مستند الشروط أو الخصوصية هو مسار الإعلان العام المدعوم. React لا يتحدث إلى Firebase ولا يخزّن بيانات FCM.',
      legalLink: 'فتح المستندات القانونية',
    },
    workbook: {
      questionnaireTitle: 'استيراد وتصدير الاستبيان',
      resultsTitle: 'ملف Excel لروابط الأنماط الثلاثة',
      downloadTemplate: 'تنزيل قالب الأسئلة',
      exportQuestionnaire: 'تصدير كل بيانات الاستبيان',
      importQuestionnaire: 'استيراد الاستبيان الكامل',
      downloadProfileTemplate: 'تنزيل قالب Excel',
      exportResults: 'تصدير بيانات Excel الحالية',
      importResults: 'استيراد ملف Excel',
      existingManagementTitle: 'إدارة الاستبيان الحالية',
      templateHint: 'القالب مخصص لإضافة الأسئلة والإجابات والأوزان والرموز المختصرة للأنماط. Laravel هو من يولّد ملف Excel ويحلّله.',
      fullExportHint: 'التصدير الكامل يمكنه تحديث البيانات الحالية وإضافة أنماط مهنية جديدة.',
      fullImportHint: 'الاستيراد الكامل يمكنه مزامنة بيانات الاستبيان الحالية وتغييرها.',
      previewHint: 'تعرض عمليات الاستيراد دائماً معاينة تحقق قبل أي تغيير في قاعدة البيانات.',
      resultsScopeHint: 'يعدّل المصنف الملخص الشخصي وعلاقات محتوى النتائج المرتبة لـ 120 رابط أعلى ثلاثة أنماط. لا يمكن إنشاء هويات الأنماط أو حذفها أو إعادة ترتيبها.',
      selectedVersion: 'إصدار التقييم المحدد',
      noVersion: 'لم يُحدد إصدار',
      versionRequired: 'حدد إصدار تقييم قبل تصدير العلاقات أو استيرادها.',
      versionLocked: 'هذا الإصدار مقفل ولا يمكن استيراده أو تصديره الآن.',
      versionStale: 'هذا الإصدار قديم. حدّثه قبل استخدام إجراءات المصنف.',
      versionIneligible: 'هذا الإصدار غير مؤهل لاستيراد المصنف أو تصديره.',
      relationshipHelpTitle: 'كيف يمثّل Excel علاقات النتائج',
      selectFile: 'ملف المصنف',
      dropzone: 'أسقط ملف .xlsx هنا أو اختره من جهازك.',
      fileRestrictions: 'ملفات .xls و.xlsm وCSV والملفات المحمية بكلمة مرور غير مدعومة. يُقبل فقط مصنف .xlsx.',
      removeFile: 'إزالة الملف',
      validatePreview: 'التحقق والمعاينة',
      commit: 'تطبيق الاستيراد',
      refreshConfiguration: 'تحديث التهيئة',
      downloadLatestExport: 'تنزيل أحدث تصدير',
      repreviewRequired: 'تم تحديث التهيئة. تحقق من المصنف مرة أخرى قبل التطبيق.',
      workbookType: 'نوع المصنف',
      schemaVersion: 'إصدار مخطط المصنف',
      supportedSchemaVersion: 'إصدار المخطط المدعوم',
      baseRevision: 'مراجعة التصدير / الأساس',
      currentRevision: 'مراجعة الخادم الحالية',
      canCommit: 'يُسمح بالتطبيق',
      fileHash: 'تجزئة الملف',
      changeSummary: 'ملخص التغييرات',
      warnings: 'تحذيرات',
      warning: 'تحذير',
      yes: 'نعم',
      no: 'لا',
      newRevision: 'مراجعة التهيئة الجديدة',
      success: 'تم تطبيق المصنف بنجاح.',
      destructiveNote: 'سيحذف هذا المصنف سجلات أو يؤرشفها أو يفصل علاقات.',
      fullSyncNote: 'يقوم هذا الاستيراد بمزامنة كاملة لجميع ترتيبات الأنماط الثلاثة وعددها 120. ستُحذف العلاقات الحالية غير الموجودة في ملف Excel.',
      destructiveConfirm: 'أفهم أن السجلات والعلاقات المدرجة سيتم تغييرها.',
      filterErrors: 'تصفية أخطاء التحقق',
      relationship: {
        multiRow: 'تُمثَّل علاقات متعدد-إلى-متعدد بصف اتصال واحد لكل علاقة، وليس بخلايا اختيار متعدد.',
        dropdowns: 'يتضمن كل صف قائمة منسدلة للنتيجة، وترتيباً، وقائمة منسدلة لمرجع المحتوى.',
        position: 'يتحكم الموضع في ترتيب مختلط واحد لنقاط القوة، وتحديات وفرص النمو المهني، والمسارات المهنية المقترحة، والمهارات الجديرة بالتطوير.',
        detach: 'إزالة صف اتصال تفصل ذلك العنصر عن رابط أعلى ثلاثة أنماط.',
        noMultiSelect: 'لا يدعم Excel خلايا الاختيار المتعدد لهذه العلاقات.',
      },
      sync: {
        immutableKeys: 'تُطابق السجلات الحالية باستخدام مفاتيح ثابتة، وليس العناوين.',
        newKeys: 'تستخدم السجلات الجديدة مفاتيح فريدة جديدة.',
        numericZero: 'القيمة الرقمية 0 هي وزن صفري حقيقي.',
        blankWeight: 'خلايا الوزن الفارغة لا تعني صفراً تلقائياً.',
        newPatternWeights: 'النمط المهني الجديد يتطلب أوزاناً لكل الإجابات المنطبقة.',
        noSilentDelete: 'الصفوف الناقصة لا تُحذف بصمت؛ يجب أن يكون الحذف أو الأرشفة صريحاً.',
      },
      results: {
        sync: {
          canonicalKey: 'يُعرَّف كل ملف بمفتاحه المعياري المرتب مثل R-I-S. R-I-S وI-S-R ملفان مختلفان.',
          personalSummaryTitle: 'اختر الملخص الشخصي من القائمة العربية. البادئة الرقمية هي معرف قاعدة البيانات. 0 — بدون ملخص شخصي يفصل الملخص.',
          relationshipArrays: 'يجب أن يحتوي كل عمود علاقة على مصفوفة JSON من المعرفات الرقمية مثل [3,5]. [] يفرّغ الفئة. الخلايا الفارغة غير صالحة.',
          catalogIds: 'ابحث عن كل معرف رقمي في ورقة الكتالوج المطابقة أو في ورقة _Lists المخفية. المعرفات مرتبطة بقاعدة البيانات والإصدار المُصدَّر.',
          blankMeansUnchanged: 'الخلايا الفارغة في أعمدة العلاقة غير صالحة. يجب أن يحتوي كل صف على JSON صريح، بما في ذلك [] عند تفريغ الفئة.',
          emptyArrayDetaches: '[] يفرّغ كل علاقة من ذلك النوع. العلاقات غير الموجودة في الملف تُحذف عند الاعتماد.',
          orderedItemRefs: 'حقل ordered_item_ids اختياري. اتركه فارغاً أو استخدم [] ليقوم النظام بترتيب العناصر تلقائياً بالترتيب التالي: نقاط القوة، ثم فرص النمو، ثم المسارات المهنية، ثم المهارات. عند إدخال مصفوفة غير فارغة فإنها تحدد ترتيب عرض مخصصاً.',
          mandatorySync: 'تستخدم جميع صفوف ترتيبات الأنماط الثلاثة وعددها 120 إجراء sync دائماً.',
          readOnlyIdentities: 'يولّد Laravel هويات الثلاثيات عند بناء الإصدار ولا يمكن إنشاؤها أو حذفها أو إعادة ترتيبها من Excel.',
          historicalSnapshots: 'تبقى لقطات نتائج المستخدمين المكتملة تاريخية.',
        },
      },
      importGuidanceTitle: 'إرشادات الاستيراد',
      steps: {
        label: 'خطوات الاستيراد',
        select: 'اختيار الملف',
        preview: 'التحقق والمعاينة',
        confirm: 'تأكيد التغييرات',
        completed: 'اكتمل',
      },
      status: {
        validating: 'جارٍ التحقق من المصنف…',
        committing: 'جارٍ تطبيق تغييرات المصنف…',
        working: 'جارٍ رفع المصنف…',
        uploadProgress: 'جارٍ الرفع {percent}%',
      },
      summary: {
        patternsCreated: 'أنماط مهنية أُنشئت',
        patternsUpdated: 'أنماط مهنية حُدّثت',
        patternsArchived: 'أنماط مهنية أُرشفت',
        patternsDeleted: 'أنماط مهنية حُذفت',
        sectionsCreated: 'أقسام أُنشئت',
        sectionsUpdated: 'أقسام حُدّثت',
        sectionsMoved: 'أقسام نُقلت',
        questionsCreated: 'أسئلة أُنشئت',
        questionsUpdated: 'أسئلة حُدّثت',
        questionsMoved: 'أسئلة نُقلت',
        questionsArchived: 'أسئلة أُرشفت',
        questionsDeleted: 'أسئلة حُذفت',
        answersCreated: 'إجابات أُنشئت',
        answersUpdated: 'إجابات حُدّثت',
        answersArchived: 'إجابات أُرشفت',
        answersDeleted: 'إجابات حُذفت',
        weightsCreated: 'أوزان أُنشئت',
        weightsUpdated: 'أوزان حُدّثت',
        unchangedRows: 'صفوف دون تغيير',
        tagsCreated: 'وسوم أُنشئت',
        tagsUpdated: 'وسوم حُدّثت',
        tagsArchived: 'وسوم أُرشفت',
        tagsDeleted: 'وسوم حُذفت',
        contentCreated: 'محتوى قابل لإعادة الاستخدام أُنشئ',
        contentUpdated: 'محتوى قابل لإعادة الاستخدام حُدّث',
        contentArchived: 'محتوى قابل لإعادة الاستخدام أُرشف',
        contentDeleted: 'محتوى قابل لإعادة الاستخدام حُذف',
        summariesCreated: 'ملخصات شخصية أُنشئت',
        summariesUpdated: 'ملخصات شخصية حُدّثت',
        summaryAttached: 'إرفاقات الملخص الشخصي',
        summaryDetached: 'فصل الملخص الشخصي',
        keyStrengthsAttached: 'إرفاقات نقاط القوة',
        keyStrengthsDetached: 'فصل نقاط القوة',
        growthAttached: 'إرفاقات فرص النمو',
        growthDetached: 'فصل فرص النمو',
        careerPathsAttached: 'إرفاقات المسارات المهنية',
        careerPathsDetached: 'فصل المسارات المهنية',
        skillsAttached: 'إرفاقات المهارات',
        skillsDetached: 'فصل المهارات',
        connectionsAdded: 'اتصالات أُضيفت',
        connectionsRemoved: 'اتصالات أُزيلت',
        connectionsReordered: 'اتصالات أُعيد ترتيبها',
        profilesExpected: 'الملفات المتوقعة',
        profilesFound: 'الملفات الموجودة',
        profilesMatched: 'الملفات المطابقة',
        profilesIgnored: 'الملفات المتجاهلة',
        profilesSynchronized: 'الملفات التي تمت مزامنتها',
        profilesChanged: 'الملفات التي ستتغير علاقاتها',
        relationshipsRemoved: 'العلاقات التي ستُحذف',
        profilesMissing: 'الملفات الناقصة',
        invalidKeys: 'مفاتيح غير صالحة',
        duplicateKeys: 'مفاتيح مكررة',
        unchangedProfiles: 'ملفات دون تغيير',
      },
      errors: {
        downloadFailed: 'تعذر تنزيل المصنف. حاول مرة أخرى.',
        noExportPermission: 'ليست لديك صلاحية تصدير هذا المصنف.',
        noImportPermission: 'ليست لديك صلاحية استيراد هذا المصنف.',
        importNotAllowed: 'هذا الإصدار لا يسمح باستيراد المصنف.',
        fileTooLarge: 'المصنف أكبر من الحد الذي يسمح به الخادم.',
        oversized: 'هذا الملف أكبر من أن يُرفع من هذه الشاشة.',
        unsupportedType: 'نوع المصنف غير مدعوم. استخدم ملف .xlsx قياسياً.',
        unsupportedUploadMediaType: 'تعذر رفع ملف Excel لأن صيغة طلب الرفع غير مدعومة. يرجى المحاولة مرة أخرى، وإذا استمرت المشكلة فتواصل مع مسؤول النظام.',
        extension: 'يُقبل فقط ملف .xlsx. ملفات .xls و.xlsm وCSV غير مدعومة.',
        validationTitle: 'تعذر تطبيق المصنف. راجع أخطاء الخلايا.',
        tableTitle: 'أخطاء التحقق من المصنف',
        sheet: 'الورقة',
        cell: 'الخلية',
        message: 'الرسالة',
        value: 'القيمة المرفوضة',
        staleRevision: 'تغيرت قاعدة البيانات بعد تصدير هذا المصنف أو معاينته.',
        staleInstructions: 'حدّث التهيئة، ونزّل أحدث تصدير عند الحاجة، ثم تحقق من الملف مرة أخرى قبل التطبيق. الملف المحدد ما زال متاحاً.',
        unsupportedSchema: 'يستخدم هذا المصنف إصدار مخطط {fileVersion}، بينما يدعم هذا الخادم الإصدار {supportedVersion}. نزّل أحدث تصدير وتحقق مرة أخرى. الملف المحدد ما زال متاحاً.',
        networkPreview: 'تعذرت المعاينة بسبب انقطاع الاتصال. يمكنك إعادة التحقق.',
        commitUnknown: 'نتيجة التطبيق غير معروفة بسبب فقدان الاتصال. حدّث التهيئة وتحقق مرة أخرى قبل تطبيق آخر.',
        malformed: 'طلب المصنف غير صالح. استخدم ملف .xlsx قياسياً مُنشأ من القالب.',
        wrongWorkbookType: 'هذا الملف ليس مصنف روابط أعلى ثلاثة أنماط.',
        versionMismatch: 'ينتمي هذا المصنف إلى إصدار تقييم مختلف.',
        staleWorkbook: 'هذا المصنف قديم. نزّل تصديرًا جديدًا ثم تحقق مرة أخرى.',
        invalidProfileIdentity: 'يستخدم أحد صفوف المصنف هوية ترتيب غير صالحة.',
        invalidContentKey: 'تستخدم إحدى خلايا المصنف مفتاح محتوى نتائج غير صالح.',
        previewFailed: 'فشلت معاينة المصنف. الملف المحدد ما زال متاحاً.',
        previewSuccess: 'اكتملت معاينة المصنف. لم يُطبَّق أي تغيير على قاعدة البيانات.',
        commitFailed: 'فشل تطبيق المصنف. الملف المحدد ما زال متاحاً.',
        commitSuccess: 'تم تطبيق تغييرات المصنف بنجاح.',
      },
      newQuestions: {
        title: 'إضافة أسئلة باستخدام Excel',
        downloadTemplate: 'تحميل قالب أسئلة جديدة',
        import: 'استيراد أسئلة جديدة',
        importTitle: 'استيراد أسئلة جديدة',
        dialogDescription: 'المعاينة لا تضيف شيئاً حتى تؤكد. تُضاف الأسئلة الجديدة تلقائياً وتبقى الأسئلة الحالية دون تغيير.',
        downloadBusy: 'جارٍ تحميل قالب الأسئلة الجديدة…',
        downloadSuccess: 'تم تحميل قالب الأسئلة الجديدة.',
        vsFullImport: 'الاستيراد الكامل يمكنه تغيير بيانات الاستبيان الحالية. استيراد الأسئلة الجديدة يضيف أسئلة فقط.',
        addOnlyHint: 'استيراد الأسئلة الجديدة لا يعدّل الأسئلة الحالية ولا يعيد ترتيبها. تُضاف الأسئلة المستوردة تلقائياً في النهاية.',
        existingUnchanged: 'الأسئلة الحالية تبقى دون تغيير.',
        appendedAutomatically: 'تُضاف الأسئلة تلقائياً إلى نهاية الأقسام المحددة.',
        explanationSelectSection: 'اختر القسم من القائمة العربية المنسدلة داخل Excel.',
        explanationTwoAnswers: 'أضف إجابتين على الأقل لكل سؤال.',
        explanationWeights: 'املأ كل خلية وزن للنمط. الخلايا الفارغة لا تُعامل صفراً.',
        explanationAppend: 'تُضاف الأسئلة تلقائياً إلى نهاية كل قسم محدد.',
        securityNotice: 'ارفع فقط مصنف .xlsx قياسياً تم تنزيله من هذه الصفحة. تُرفض ملفات الماكرو وCSV و.xls وZIP.',
        selectFile: 'اختيار المصنف',
        replaceFile: 'استبدال الملف المحدد',
        clearFile: 'مسح التحديد',
        preview: 'معاينة',
        previewing: 'جارٍ المعاينة…',
        previewSummary: 'ملخص المعاينة',
        previewState: 'حالة المعاينة',
        valid: 'صالح',
        invalid: 'غير صالح',
        questionsCount: 'الأسئلة',
        answersCount: 'الإجابات',
        sectionsCount: 'الأقسام المتأثرة',
        errorsCount: 'الأخطاء',
        warningsCount: 'التحذيرات',
        questionSummaries: 'ملخصات الأسئلة',
        sectionAr: 'القسم بالعربية',
        answerCount: 'عدد الإجابات',
        weightsComplete: 'اكتمال أوزان الأنماط',
        questionRef: 'مرجع السؤال',
        excelRow: 'صف Excel',
        column: 'العمود',
        templateVersion: 'إصدار المصنف / القالب',
        staleTemplate: 'هذا القالب قديم. حمّل قالباً جديداً ثم عاين مرة أخرى. التطبيق محظور.',
        patternSetChanged: 'تغيرت مجموعة الأنماط المهنية بعد إنشاء هذا القالب. حمّل قالباً جديداً.',
        downloadFresh: 'تحميل قالب جديد',
        commit: 'اعتماد الأسئلة الجديدة',
        confirmCopy: 'سيتم إنشاء الأسئلة والإجابات الجديدة وإضافتها إلى نهاية الأقسام المحددة. لن يتم تعديل الأسئلة الحالية.',
        confirmLabel: 'أفهم أن هذه الأسئلة ستُضاف فقط، وأن الأسئلة الحالية ستبقى دون تغيير.',
        success: 'تم استيراد {questions} أسئلة و{answers} إجابات.',
        failure: 'تعذّر إكمال استيراد الأسئلة الجديدة.',
        createdQuestions: 'الأسئلة التي أُنشئت',
        createdAnswers: 'الإجابات التي أُنشئت',
        unsupportedFile: 'ملف غير مدعوم. يُقبل فقط مصنف .xlsx.',
        errors: {
          expiredPreview: 'انتهت صلاحية هذه المعاينة. ارفع المصنف وعاينه مرة أخرى قبل الاعتماد.',
        },
      },
    },
    pagination: {
      page: 'صفحة',
      of: 'من',
      perPage: 'لكل صفحة',
      previous: 'الصفحة السابقة',
      next: 'الصفحة التالية',
    },
    filters: {
      all: 'الكل',
      active: 'نشط',
      inactive: 'غير نشط',
    },
  },
};

export const messages = applyCompanyPlanMessages(baseMessages);

export function translate(locale, key, vars = {}) {
  const source = messages[locale] || messages.en;
  const value = key.split('.').reduce((acc, part) => (acc == null ? acc : acc[part]), source);
  if (typeof value !== 'string') {
    return key;
  }
  return Object.entries(vars).reduce(
    (text, [name, replacement]) => text.replaceAll(`{${name}}`, String(replacement)),
    value,
  );
}
